import { Loan } from '@/types/nsldsTypes';
import { PlanType } from '@/types/planTypes';
import { parse, isBefore, isAfter, isEqual, isValid, format } from 'date-fns';
import { formatCurrency } from '@/utils/formatters';
// Import shared types
import { Explanation, PlanCriterionResult } from '@/types/eligibilityTypes';

/**
 * Interface defining the inputs required to determine loan plan eligibility.
 */
interface EligibilityInput {
  loan: Loan;
  allLoans: Loan[]; // Full loan portfolio for the borrower
  income: number; // Borrower's Annual Gross Income
  state: string; // Borrower's state of residence (used for poverty guidelines)
  pslfEligible: boolean; // From user survey data
  meetsFinancialHardship: boolean | null; // Calculated in context
}

/**
 * Parses a loan date string (MM/DD/YYYY), handling potential invalid formats.
 * @param dateStr The date string (e.g., "MM/DD/YYYY").
 * @returns A Date object or null if invalid.
 */
function parseLoanDate(dateStr: string | undefined | null): Date | null {
  if (!dateStr) return null;
  // Use a reference date (like today) for parsing, although it doesn't strictly matter for MM/DD/YYYY
  const parsedDate = parse(dateStr, 'MM/dd/yyyy', new Date());
  return isValid(parsedDate) ? parsedDate : null;
}

/**
 * Checks if the borrower meets the "new borrower" criteria specifically for PAYE.
 * PAYE: No federal loans before 10/1/2007 AND received a Direct loan disbursement >= 10/1/2011.
 */
function checkIsNewBorrowerForPAYE(allLoans: Loan[]): {
  isNewBorrower: boolean;
  explanation: Explanation;
} {
  const PAYE_FIRST_LOAN_CUTOFF = parse('2007-10-01', 'yyyy-MM-dd', new Date());
  const PAYE_DIRECT_DISBURSEMENT_CUTOFF = parse('2011-10-01', 'yyyy-MM-dd', new Date());
  let hadLoanBeforeFirstCutoff = false;
  let hadDirectDisbursementAfterSecondCutoff = false;
  let earliestLoanDate: Date | null = null;
  let earliestDirectDisbursementAfterCutoff: Date | null = null;

  for (const l of allLoans) {
    const loanDate = parseLoanDate(l['Loan Date']);
    if (!loanDate) continue;

    if (!earliestLoanDate || isBefore(loanDate, earliestLoanDate)) {
      earliestLoanDate = loanDate;
    }

    if (isBefore(loanDate, PAYE_FIRST_LOAN_CUTOFF)) {
      hadLoanBeforeFirstCutoff = true;
    }

    if (
      l['Loan Type Group'] === 'Direct' &&
      (isAfter(loanDate, PAYE_DIRECT_DISBURSEMENT_CUTOFF) ||
        isEqual(loanDate, PAYE_DIRECT_DISBURSEMENT_CUTOFF))
    ) {
      hadDirectDisbursementAfterSecondCutoff = true;
      if (
        !earliestDirectDisbursementAfterCutoff ||
        isBefore(loanDate, earliestDirectDisbursementAfterCutoff)
      ) {
        earliestDirectDisbursementAfterCutoff = loanDate;
      }
    }
  }

  const isNewBorrower = !hadLoanBeforeFirstCutoff && hadDirectDisbursementAfterSecondCutoff;
  let explanation: Explanation;

  if (isNewBorrower) {
    explanation = {
      friendly: "You appear to meet the 'new borrower' requirements for the PAYE plan.",
      technical: `PAYE New Borrower Status: Met. No federal loans found before ${format(
        PAYE_FIRST_LOAN_CUTOFF,
        'yyyy-MM-dd'
      )} and had a Direct loan disbursement on or after ${format(
        PAYE_DIRECT_DISBURSEMENT_CUTOFF,
        'yyyy-MM-dd'
      )}.`,
    };
  } else {
    if (hadLoanBeforeFirstCutoff) {
      explanation = {
        friendly: `The PAYE plan requires that you didn't have any federal student loans before ${format(
          PAYE_FIRST_LOAN_CUTOFF,
          'MMMM d, yyyy'
        )}. Our records show a loan disbursed before this date.`,
        technical: `PAYE New Borrower Failure: Had a federal loan disbursed before ${format(
          PAYE_FIRST_LOAN_CUTOFF,
          'yyyy-MM-dd'
        )}. First loan date: ${earliestLoanDate ? format(earliestLoanDate, 'yyyy-MM-dd') : 'N/A'}.`,
      };
    } else if (!hadDirectDisbursementAfterSecondCutoff) {
      explanation = {
        friendly: `The PAYE plan also requires that you received a Direct Loan on or after ${format(
          PAYE_DIRECT_DISBURSEMENT_CUTOFF,
          'MMMM d, yyyy'
        )}. Our records don't show one disbursed by then.`,
        technical: `PAYE New Borrower Failure: Did not receive a Direct Loan disbursement on or after ${format(
          PAYE_DIRECT_DISBURSEMENT_CUTOFF,
          'yyyy-MM-dd'
        )}. Earliest qualifying Direct disbursement: ${
          earliestDirectDisbursementAfterCutoff
            ? format(earliestDirectDisbursementAfterCutoff, 'yyyy-MM-dd')
            : 'None found'
        }.`,
      };
    } else {
      explanation = {
        friendly:
          "You don't appear to meet the specific 'new borrower' criteria for the PAYE plan based on your loan history.",
        technical: `PAYE New Borrower Failure: Does not meet criteria for undetermined reason (Inconsistent state: hadLoanBeforeFirstCutoff=${String(
          hadLoanBeforeFirstCutoff
        )}, hadDirectDisbursementAfterSecondCutoff=${String(
          hadDirectDisbursementAfterSecondCutoff
        )}).`,
      };
    }
  }

  return { isNewBorrower, explanation };
}

/**
 * Estimates if the borrower likely had an outstanding balance on any federal loan on a specific date.
 * This is an estimation based on available loan data (disbursement date, current status, current balance).
 * It cannot definitively determine the balance on a past date due to data granularity.
 *
 * @param allLoans The borrower's full loan portfolio.
 * @param targetDate The date to check for an outstanding balance (e.g., IBR cutoff).
 * @returns True if it's likely there was an outstanding balance, False otherwise.
 */
function estimateHadOutstandingBalanceOnDate(allLoans: Loan[], targetDate: Date): boolean {
  for (const l of allLoans) {
    const loanDate = parseLoanDate(l['Loan Date']);
    if (!loanDate) continue;

    if (isBefore(loanDate, targetDate)) {
      const currentBalance = l['Total Outstanding'];
      const status = l['Current Loan Status Description'].toUpperCase();
      const isLikelyPaidOff =
        status.includes('PAID IN FULL') ||
        status.includes('CONSOLIDATED') ||
        status.includes('DISCHARGED');

      if (currentBalance > 0 || !isLikelyPaidOff) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Checks if the borrower meets the "new borrower" criteria specifically for IBR.
 * New IBR: No outstanding balance on any Direct/FFEL loan on 07/01/2014 AND first Direct/FFEL disbursement >= 07/01/2014.
 * Uses estimation for the outstanding balance check.
 */
function checkIsNewBorrowerForIBR(allLoans: Loan[]): {
  isNewBorrower: boolean;
  explanation: Explanation;
} {
  const IBR_NEW_BORROWER_CUTOFF = parse('2014-07-01', 'yyyy-MM-dd', new Date());
  let firstDirectOrFFELLoanDate: Date | null = null;
  let hadDirectOrFFELDisbursementOnOrAfterCutoff = false;

  const relevantLoans = allLoans.filter(
    (l) => l['Loan Type Group'] === 'Direct' || l['Loan Type Group'] === 'FFEL'
  );

  for (const l of relevantLoans) {
    const loanDate = parseLoanDate(l['Loan Date']);
    if (!loanDate) continue;

    if (!firstDirectOrFFELLoanDate || isBefore(loanDate, firstDirectOrFFELLoanDate)) {
      firstDirectOrFFELLoanDate = loanDate;
    }
    if (isAfter(loanDate, IBR_NEW_BORROWER_CUTOFF) || isEqual(loanDate, IBR_NEW_BORROWER_CUTOFF)) {
      hadDirectOrFFELDisbursementOnOrAfterCutoff = true;
    }
  }

  const hadOutstandingBalanceOnCutoff = estimateHadOutstandingBalanceOnDate(
    relevantLoans,
    IBR_NEW_BORROWER_CUTOFF
  );

  const isNewBorrower =
    !hadOutstandingBalanceOnCutoff && hadDirectOrFFELDisbursementOnOrAfterCutoff;

  let friendlyText: string;
  let technicalText: string;

  if (isNewBorrower) {
    friendlyText =
      "You may qualify as a 'new borrower' for IBR (first loan after July 1, 2014, with no prior balance).";
    technicalText = `Met IBR 'new borrower' criteria: Estimated no outstanding Direct/FFEL balance on ${format(
      IBR_NEW_BORROWER_CUTOFF,
      'yyyy-MM-dd'
    )} AND first Direct/FFEL disbursement (${
      firstDirectOrFFELLoanDate ? format(firstDirectOrFFELLoanDate, 'yyyy-MM-dd') : 'N/A'
    }) was on or after the cutoff. Qualifies for New IBR (10%/20yr) terms.`;
  } else {
    friendlyText =
      'You may have had Direct / FFEL federal loans before July 1, 2014, meaning you would not qualify as a "new borrower". You may still be eligible for the old borrower terms.';
    technicalText = "Did not meet IBR 'new borrower' criteria.";
    if (hadOutstandingBalanceOnCutoff) {
      technicalText += ` Estimated outstanding Direct/FFEL balance found on ${format(
        IBR_NEW_BORROWER_CUTOFF,
        'yyyy-MM-dd'
      )}.`;
    } else if (!hadDirectOrFFELDisbursementOnOrAfterCutoff && firstDirectOrFFELLoanDate) {
      technicalText += ` First Direct/FFEL disbursement (${format(
        firstDirectOrFFELLoanDate,
        'yyyy-MM-dd'
      )}) was before the cutoff.`;
    } else {
      technicalText +=
        ' Could not determine first Direct/FFEL loan date or estimate balance conclusively.';
    }
    technicalText += ' Qualifies for Legacy IBR (15%/25yr) terms.';
  }

  technicalText += ' Note: Outstanding balance check is an estimation based on available data.';

  return { isNewBorrower, explanation: { friendly: friendlyText, technical: technicalText } };
}

/**
 * Determines the eligibility criteria results for all federal student loan repayment plans
 * for a *single* given loan based on its type and the borrower's overall loan portfolio characteristics.
 *
 * @param input - An object containing the specific loan, the full loan portfolio, and other relevant factors.
 * @returns An array of PlanCriterionResult objects detailing the outcome of each eligibility check for each plan.
 */
export function getEligiblePlans(input: EligibilityInput): PlanCriterionResult[] {
  const { loan, allLoans, pslfEligible, meetsFinancialHardship } = input;
  const results: PlanCriterionResult[] = [];

  // --- Helper to add results ---
  function addCriterionResult(
    plan: PlanType | PlanType[],
    criterion: string,
    isMet: boolean,
    explanation: Explanation
  ) {
    const plansArray = Array.isArray(plan) ? plan : [plan];
    plansArray.forEach((p) => {
      // Avoid adding duplicate criteria checks for the same plan
      if (!results.some((res) => res.plan === p && res.criterion === criterion)) {
        results.push({
          plan: p,
          criterion: criterion,
          isMet: isMet,
          explanationFriendly: explanation.friendly,
          technicalReason: explanation.technical,
        });
      }
    });
  }

  // --- Pre-computation ---
  const loanTypeGroup = loan['Loan Type Group'];
  const loanTypeCode = loan['Loan Type Code'];
  // Consolidated check for any Parent PLUS loan type
  const isParentPlus =
    loanTypeGroup === 'Direct Plus Parent' || loanTypeGroup === 'FFEL Plus Parent';
  const isConsolidatedLoan = loanTypeCode === 'D0' || loanTypeCode === 'D7';

  let containsParentPlus = false;
  let containsParentPlusDetermination: Explanation | null = null;
  if (isConsolidatedLoan) {
    const indicator = loan['Consolidation Loan With Any Parent Plus Indicator'];
    if (indicator === 'Yes') {
      containsParentPlus = true;
      containsParentPlusDetermination = {
        friendly:
          'This Consolidation Loan includes Parent PLUS loans. This limits IDR options, usually to ICR.',
        technical:
          "Consolidation Loan Parent Plus Status: Contains Parent PLUS (Indicator='Yes'). Restricts IDR eligibility (except ICR/ICR-PSLF).",
      };
    } else if (indicator === 'No') {
      containsParentPlus = false;
      containsParentPlusDetermination = {
        friendly:
          "This Consolidation Loan doesn't include Parent PLUS loans, allowing more IDR options.",
        technical:
          "Consolidation Loan Parent Plus Status: No Parent PLUS (Indicator='No'). Allows wider IDR eligibility.",
      };
    } else {
      containsParentPlus = false;
      containsParentPlusDetermination = {
        friendly:
          "Could not determine if this Consolidation Loan includes Parent PLUS loans. Assuming it doesn't, but if it does, IDR options may be limited.",
        technical:
          "Consolidation Loan Parent Plus Status: Unknown (Indicator missing/invalid). Assuming 'No'. If 'Yes', IDR eligibility restricted.",
      };
    }
  }

  const totalDirectLoanDebt = allLoans
    .filter((l) => l['Loan Type Group'].startsWith('Direct'))
    .reduce((sum, l) => sum + l['Total Outstanding'], 0);

  const financialHardshipResult: Explanation = {
    friendly:
      meetsFinancialHardship === true
        ? 'You appear to meet the "partial financial hardship" requirement.'
        : meetsFinancialHardship === false
        ? 'You do not appear to meet the "partial financial hardship" requirement.'
        : 'Could not determine if you meet the "partial financial hardship" requirement.',
    technical: `Financial Hardship Status: ${String(
      meetsFinancialHardship
    )}. Compared 10yr Standard payment vs 15% discretionary income.`,
  };

  const payeNewBorrowerCheck = checkIsNewBorrowerForPAYE(allLoans);
  const isPayeNewBorrower = payeNewBorrowerCheck.isNewBorrower;

  const ibrNewBorrowerCheck = checkIsNewBorrowerForIBR(allLoans);

  // --- Eligibility Checks for Each Plan Type ---

  const allPlanTypes = Object.values(PlanType);

  allPlanTypes.forEach((plan) => {
    // --- Consolidation Parent PLUS Check ---
    // Add the determination result if it was calculated (i.e., is a consolidated loan)
    if (containsParentPlusDetermination) {
      let isMetForPlan = true; // Default pass unless plan is specifically restricted
      if (
        [
          PlanType.SAVE,
          PlanType.PAYE,
          PlanType.NEW_IBR,
          PlanType.SAVE_PSLF,
          PlanType.PAYE_PSLF,
          PlanType.NEW_IBR_PSLF,
          PlanType.OLD_IBR_PSLF,
        ].includes(plan)
      ) {
        isMetForPlan = !containsParentPlus; //Containing PP fails the check for these plans
      }
      addCriterionResult(
        plan,
        'Consolidation Loan Content Check',
        isMetForPlan,
        containsParentPlusDetermination
      );
    }

    // --- Criterion: SAVE Plan Availability ---
    if (plan === PlanType.SAVE || plan === PlanType.SAVE_PSLF) {
      addCriterionResult(plan, 'SAVE Plan Availability (Court Injunction)', false, {
        friendly: 'The SAVE plan is currently subject to court injunctions and is unavailable.',
        technical: 'SAVE Plan Status: Currently blocked by court injunction (Late June 2024).',
      });
    }

    // --- Criterion: Loan Type Eligibility ---
    let loanTypeCheckMet = true;
    let loanTypeExplanation: Explanation | null = null;
    const isDirectNonParentPlus = loanTypeGroup === 'Direct';
    const isDirectConsolidationNonParentPlus = isConsolidatedLoan && !containsParentPlus;
    const isFFELNonParentPlus = loanTypeGroup === 'FFEL';

    switch (plan) {
      case PlanType.STANDARD_10_YEAR:
      case PlanType.GRADUATED: {
        // Reinstate the original check
        const eligibleGroups = [
          'Direct',
          'FFEL',
          'Direct Plus Parent',
          'FFEL Plus Parent',
          'Direct Plus Consolidation',
        ];
        if (!eligibleGroups.includes(loanTypeGroup)) {
          loanTypeCheckMet = false;
          loanTypeExplanation = {
            friendly: `${String(
              loanTypeGroup
            )} loans are not eligible for the Standard/Graduated plan.`,
            technical: `Loan Type Fail (Standard/Graduated): Group '${String(loanTypeGroup)}'.`,
          };
        } else {
          // Explicitly set as met with a specific friendly explanation
          loanTypeCheckMet = true;
          loanTypeExplanation = {
            friendly: `These types of loans are eligible for the standard and graduated 10-year repayment plans, assuming you meet other eligibility criteria.`,
            technical: `Loan Type Check Pass (Standard/Graduated): Group '${String(
              loanTypeGroup
            )}'.`,
          };
        }
        break;
      }
      case PlanType.EXTENDED_25_YEAR:
      case PlanType.EXTENDED_GRADUATED: {
        // Extended plans are generally for Direct Loans. FFEL must be consolidated.
        if (!loanTypeGroup.startsWith('Direct') && loanTypeGroup !== 'FFEL') {
          loanTypeCheckMet = false;
          loanTypeExplanation = {
            friendly: 'Extended plans are only available for Direct Loans.',
            technical: `Loan Type Fail: Group '${String(loanTypeGroup)}' not Direct.`,
          };
        } else if (loanTypeGroup === 'FFEL') {
          loanTypeCheckMet = false; // Mark as ineligible *unless* consolidated (which we can't check here)
          loanTypeExplanation = {
            friendly: 'FFEL loans must be consolidated into a Direct Loan to use Extended plans.',
            technical: 'Loan Type Fail (for Extended): FFEL must be consolidated into Direct Loan.',
          };
        }
        break;
      }
      case PlanType.SAVE:
      case PlanType.SAVE_PSLF: {
        // Note: Even if type is eligible, SAVE Availability check above makes plan ineligible
        if (isParentPlus) {
          loanTypeCheckMet = false;
          loanTypeExplanation = {
            friendly: 'Parent PLUS loans are ineligible for the SAVE plan.',
            technical: 'Loan Type Fail (SAVE): Parent PLUS.',
          };
        } else if (!isDirectNonParentPlus && !isDirectConsolidationNonParentPlus) {
          loanTypeCheckMet = false;
          if (loanTypeGroup.startsWith('FFEL'))
            loanTypeExplanation = {
              friendly: 'FFEL loans require consolidation to be eligible for SAVE.',
              technical: 'Loan Type Fail (SAVE): FFEL requires consolidation.',
            };
          else if (isConsolidatedLoan && containsParentPlus)
            loanTypeExplanation = {
              friendly: 'Consolidation loans containing Parent PLUS loans are ineligible for SAVE.',
              technical: 'Loan Type Fail (SAVE): Consolidation w/ PP.',
            };
          else
            loanTypeExplanation = {
              friendly: `Ineligible loan type (${String(loanTypeGroup)}) for SAVE.`,
              technical: `Loan Type Fail (SAVE): Group '${String(loanTypeGroup)}'.`,
            };
        }
        break;
      }
      case PlanType.PAYE:
      case PlanType.PAYE_PSLF: {
        if (isParentPlus) {
          loanTypeCheckMet = false;
          loanTypeExplanation = {
            friendly: 'Parent PLUS loans are ineligible for the PAYE plan.',
            technical: 'Loan Type Fail (PAYE): Parent PLUS.',
          };
        } else if (!isDirectNonParentPlus && !isDirectConsolidationNonParentPlus) {
          loanTypeCheckMet = false;
          if (loanTypeGroup.startsWith('FFEL'))
            loanTypeExplanation = {
              friendly: 'FFEL loans must be consolidated into a Direct Loan to use the PAYE plan.',
              technical: 'Loan Type Fail (PAYE): FFEL requires consolidation.',
            };
          else if (isConsolidatedLoan && containsParentPlus)
            loanTypeExplanation = {
              friendly: 'Consolidation loans containing Parent PLUS loans are ineligible for PAYE.',
              technical: 'Loan Type Fail (PAYE): Consolidation w/ PP.',
            };
          else
            loanTypeExplanation = {
              friendly: `Ineligible loan type (${String(loanTypeGroup)}) for PAYE.`,
              technical: `Loan Type Fail (PAYE): Group '${String(loanTypeGroup)}'.`,
            };
        }
        break;
      }
      case PlanType.NEW_IBR:
      case PlanType.NEW_IBR_PSLF:
      case PlanType.OLD_IBR:
      case PlanType.OLD_IBR_PSLF: {
        if (isParentPlus) {
          loanTypeCheckMet = false;
          loanTypeExplanation = {
            friendly: 'Parent PLUS loans are ineligible for the IBR plan.',
            technical: 'Loan Type Fail (IBR): Parent PLUS.',
          };
        } else if (
          !isDirectNonParentPlus &&
          !isDirectConsolidationNonParentPlus &&
          !isFFELNonParentPlus
        ) {
          loanTypeCheckMet = false;
          // Handle only the Consolidation w/ PP case here now
          if (isConsolidatedLoan && containsParentPlus)
            loanTypeExplanation = {
              friendly: 'Consolidation loans containing Parent PLUS loans are ineligible for IBR.',
              technical: 'Loan Type Fail (IBR): Consolidation w/ PP.',
            };
          else
            loanTypeExplanation = {
              friendly: `Ineligible loan type (${String(loanTypeGroup)}) for IBR.`,
              technical: `Loan Type Fail (IBR): Group '${String(loanTypeGroup)}'.`,
            };
        }
        // PSLF-specific check (only for FFEL on OLD_IBR_PSLF) handled below
        break;
      }
      case PlanType.ICR:
      case PlanType.ICR_PSLF: {
        // Check Direct Parent PLUS specifically for consolidation need
        if (loanTypeGroup === 'Direct Plus Parent') {
          loanTypeCheckMet = false; // Needs consolidation for ICR
          loanTypeExplanation = {
            friendly:
              'Direct Parent PLUS loans must be consolidated into a Direct Loan to use the ICR plan.',
            technical: 'Loan Type Fail (ICR): Direct PP (needs consolidation).',
          };
        } else if (loanTypeGroup === 'FFEL Plus Parent') {
          loanTypeCheckMet = false; // Needs consolidation for ICR
          loanTypeExplanation = {
            friendly:
              'FFEL Parent PLUS loans must be consolidated into a Direct Loan to use the ICR plan.',
            technical: 'Loan Type Fail (ICR): FFEL PP (needs consolidation).',
          };
        } else if (loanTypeGroup.startsWith('FFEL')) {
          // Non-Parent FFEL
          loanTypeCheckMet = false;
          loanTypeExplanation = {
            friendly: 'FFEL loans must be consolidated into a Direct Loan to use the ICR plan.',
            technical: 'Loan Type Fail (ICR): FFEL (needs consolidation).',
          };
        } else if (!isDirectNonParentPlus && !isConsolidatedLoan) {
          // Other non-Direct, non-Consolidated types
          loanTypeCheckMet = false;
          loanTypeExplanation = {
            friendly: `${String(loanTypeGroup)} loans are ineligible for ICR.`,
            technical: `Loan Type Fail (ICR): Group '${String(loanTypeGroup)}'.`,
          };
        } else {
          // Eligible: Direct non-PP, Direct Consolidation (incl. those w/ PP)
          loanTypeCheckMet = true;
          loanTypeExplanation = {
            friendly: `These types of loans are generally eligible for the ICR plan (Parent PLUS loans require consolidation first).`,
            technical: `Loan Type Check Pass (ICR): Group '${String(
              loanTypeGroup
            )}' is eligible or needs consolidation.`,
          };
        }
        // Note: Interaction with PSLF/Consolidation+PP handled below and in PSLF checks
        break;
      }
    }

    // Add the criterion result regardless of whether it was met or not, if an explanation exists.
    if (loanTypeExplanation) {
      addCriterionResult(plan, 'Loan Type Eligibility', loanTypeCheckMet, loanTypeExplanation);
    }

    // --- Criterion: Debt Threshold (Extended Plans) ---
    if (plan === PlanType.EXTENDED_25_YEAR || plan === PlanType.EXTENDED_GRADUATED) {
      const debtThresholdCheckMet = totalDirectLoanDebt > 30000;
      addCriterionResult(plan, 'Minimum Debt Threshold ($30k Direct)', debtThresholdCheckMet, {
        friendly: debtThresholdCheckMet
          ? `Direct Loan balance (${formatCurrency(totalDirectLoanDebt)}) meets $30k minimum.`
          : `Direct Loan balance (${formatCurrency(totalDirectLoanDebt)}) below $30k minimum.`,
        technical: `Extended Debt Check: ${formatCurrency(totalDirectLoanDebt)} ${
          debtThresholdCheckMet ? '>' : '<='
        } $30,000. Met: ${String(debtThresholdCheckMet)}.`,
      });
    }

    // --- Criterion: Financial Hardship (PAYE & IBR Plans) ---
    if (
      plan === PlanType.PAYE ||
      plan === PlanType.PAYE_PSLF ||
      plan === PlanType.NEW_IBR ||
      plan === PlanType.NEW_IBR_PSLF ||
      plan === PlanType.OLD_IBR ||
      plan === PlanType.OLD_IBR_PSLF
    ) {
      const hardshipCheckMet = meetsFinancialHardship === true;
      addCriterionResult(
        plan,
        'Partial Financial Hardship',
        hardshipCheckMet,
        financialHardshipResult
      );
    }

    // --- Criterion: PAYE New Borrower Status ---
    if (plan === PlanType.PAYE || plan === PlanType.PAYE_PSLF) {
      const payeNewBorrowerCheckMet = isPayeNewBorrower;
      addCriterionResult(
        plan,
        'PAYE New Borrower Requirements',
        payeNewBorrowerCheckMet,
        payeNewBorrowerCheck.explanation
      );
    }

    // --- Criterion: IBR New Borrower Status (Blocking for New vs Old IBR) ---
    if (plan === PlanType.NEW_IBR || plan === PlanType.NEW_IBR_PSLF) {
      addCriterionResult(
        plan,
        'IBR New Borrower Requirement (for 10%/20yr)',
        ibrNewBorrowerCheck.isNewBorrower,
        ibrNewBorrowerCheck.explanation
      );
    } else if (plan === PlanType.OLD_IBR || plan === PlanType.OLD_IBR_PSLF) {
      // For Old IBR, the requirement is that the borrower is *NOT* a new IBR borrower
      addCriterionResult(
        plan,
        'IBR Old Borrower Requirement (for 15%/25yr)',
        !ibrNewBorrowerCheck.isNewBorrower,
        ibrNewBorrowerCheck.explanation
      );
    }

    // --- Criteria: PSLF ---
    if (
      [
        PlanType.SAVE_PSLF,
        PlanType.PAYE_PSLF,
        PlanType.NEW_IBR_PSLF,
        PlanType.OLD_IBR_PSLF,
        PlanType.ICR_PSLF,
      ].includes(plan)
    ) {
      // 1. PSLF Flag Check
      const pslfFlagCheckMet = pslfEligible;
      addCriterionResult(plan, 'PSLF Eligibility Indicated', pslfFlagCheckMet, {
        friendly: pslfEligible
          ? `You said that ${
              loan['Borrower'] === 'user' ? 'you are' : 'your partner is'
            } eligible for PSLF.`
          : `You said that ${
              loan['Borrower'] === 'user' ? 'you are' : 'your partner is'
            } not eligible for PSLF.`,
        technical: `PSLF Flag Check: pslfEligible=${String(pslfEligible)}.`,
      });

      // 2. PSLF Loan Type Check (Direct Loan Required, specific consolidation rules)
      let pslfLoanTypeCheckMet = true; // Assume true initially
      let pslfLoanTypeExplanation: Explanation = {
        friendly: 'Direct Loans are generally eligible for PSLF.', // Default success assumption for Direct
        technical: 'PSLF Loan Type Pass: Eligible Direct Loan type.',
      };

      if (!loanTypeGroup.startsWith('Direct')) {
        // Non-Direct Loans (FFEL, Perkins, etc.) always require consolidation for PSLF
        pslfLoanTypeCheckMet = false;
        if (loanTypeGroup.startsWith('FFEL')) {
          // Covers FFEL and FFEL Plus Parent
          pslfLoanTypeExplanation = {
            friendly:
              'FFEL loans (including Parent PLUS) must be consolidated into a Direct Loan to be eligible for PSLF.',
            technical: 'PSLF Loan Type Fail: FFEL requires consolidation for PSLF.',
          };
        } else if (loanTypeGroup === 'Perkins') {
          pslfLoanTypeExplanation = {
            friendly:
              'Perkins loans must be consolidated into a Direct Loan to be eligible for PSLF.',
            technical: 'PSLF Loan Type Fail: Perkins requires consolidation for PSLF.',
          };
        } else {
          // Other non-Direct types
          pslfLoanTypeExplanation = {
            friendly: 'This loan type is not eligible for PSLF, even after consolidation.', // Adjust if other types can be consolidated
            technical: `PSLF Loan Type Fail: Group '${String(loanTypeGroup)}' ineligible.`,
          };
        }
      } else if (loanTypeGroup === 'Direct Plus Parent') {
        // Direct Parent PLUS loans require consolidation for PSLF
        pslfLoanTypeCheckMet = false;
        pslfLoanTypeExplanation = {
          friendly:
            'Direct Parent PLUS loans must be consolidated into a Direct Loan to be eligible for PSLF.',
          technical: 'PSLF Loan Type Fail: Direct PP requires consolidation for PSLF.',
        };
      } else if (isConsolidatedLoan && containsParentPlus) {
        // Consolidated loans containing Parent PLUS are *only* eligible for PSLF under ICR
        if (plan !== PlanType.ICR_PSLF) {
          pslfLoanTypeCheckMet = false;
          pslfLoanTypeExplanation = {
            friendly:
              'Consolidation loans containing Parent PLUS loans are only eligible for PSLF under the ICR plan.',
            technical: `PSLF Loan Type Fail (Plan ${plan}): Consolidation w/ PP restricted to ICR-PSLF.`,
          };
        } else {
          // Plan IS ICR_PSLF, so this loan type passes the PSLF check here
          pslfLoanTypeCheckMet = true;
          pslfLoanTypeExplanation = {
            friendly:
              'Consolidation loans containing Parent PLUS loans are eligible for PSLF under the ICR plan.',
            technical: 'PSLF Loan Type Pass (for ICR-PSLF): Consolidation w/ PP.',
          };
        }
      }
      // Note: The base loan type check for SAVE/PAYE/IBR already handles the primary ineligibility
      // of Parent PLUS or Consolidation w/ PP for those plans. This PSLF check focuses specifically
      // on PSLF's own requirements (Direct Loan, consolidation needs).

      addCriterionResult(
        plan,
        'PSLF Loan Type Eligibility (Direct Required)',
        pslfLoanTypeCheckMet,
        pslfLoanTypeExplanation
      );
    }

    // --- Criterion: Private Refinance Lender Approval ---
    if (plan === PlanType.PRIVATE_REFINANCE) {
      addCriterionResult(plan, 'Lender Approval Required', true, {
        // Always required
        friendly: 'Requires lender approval based on creditworthiness.',
        technical: 'Private Refi Check: Requires lender underwriting.',
      });
    }
  });

  return results;
}

export const CALCULATION_ASSUMPTIONS = {
  refiRateAssumption: 0.07,
  refiMonthsAssumption: 132,
  degreeTypeAssumption: 'graduate' as 'graduate' | 'undergraduate',
  simulationYears: 30,
};
